import { AfterViewInit, Component, Injector, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import ClientAddPopupComponent from '@app/components/clients/add-popup/client-add-popup.component';
import LayoutService from '@app/services/layout.service';
import SidebarService from '@app/services/sidebar.service';
import { DrawerComponent, MenuComponent, ScrollComponent, ToggleComponent } from '@metronic/app/kt/components';
import AppComponentBase from '@shared/common/app-component-base';
import { FormattedStringValueExtracter } from '@shared/helpers/FormattedStringValueExtracter';
import { UniqueObjectSet } from '@shared/helpers/UniqueObjectSet';
import { PermissionCheckerService } from 'abp-ng2-module';
import { isNil } from 'lodash';
import objectPath from 'object-path';
import { filter } from 'rxjs/operators';

import AppMenu from './app-menu';
import AppMenuItem from './app-menu-item';
import AppNavigationService from './app-navigation.service';

@Component({
    selector: 'side-bar-menu',
    templateUrl: './side-bar-menu.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
class SideBarMenuComponent extends AppComponentBase implements OnInit, AfterViewInit {
    @Input() iconMenu = false;

    @Input() menuClass = 'menu menu-column menu-rounded px-3';

    @Input() openAccordions: UniqueObjectSet<AppMenuItem>;

    menu: AppMenu = null;

    currentRouteUrl = '';

    isMobile = false;

    constructor(
        injector: Injector,
        private dialog: MatDialog,
        private router: Router,
        public permission: PermissionCheckerService,
        private appNavigationService: AppNavigationService,
        private sidebarService: SidebarService,
        private layoutService: LayoutService,
    ) {
        super(injector);
    }

    ngOnInit() {
        this.menu = this.appNavigationService.getMenu();

        this.currentRouteUrl = this.router.url.split(/[?#]/)[0];

        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe(() => this.currentRouteUrl = this.router.url.split(/[?#]/)[0]);

        this.layoutService.isMobile$.subscribe((isMobile) => {
            this.isMobile = isMobile;
        });

        setTimeout(() => {
            MenuComponent.reinitialization();
            DrawerComponent.reinitialization();
            ToggleComponent.reinitialization();
            ScrollComponent.reinitialization();
        }, 50);
    }

    ngAfterViewInit(): void {
        this.scrollToCurrentMenuElement();
    }

    reinitializeMenu(): void {
        this.menu = this.appNavigationService.getMenu();
        this.currentRouteUrl = this.router.url.split(/[?#]/)[0];
    }

    openAddClientPopup(): void {
        this.dialog.open(ClientAddPopupComponent);
    }

    showMenuItem(menuItem: AppMenuItem): boolean {
        return this.appNavigationService.showMenuItem(menuItem);
    }

    isMenuItemIsActive(item: AppMenuItem): boolean {

        if (!item.route) {
            return false;
        }

        const urlTree = this.router.parseUrl(this.currentRouteUrl.replace(/\/$/, ''));
        const urlString = '/' + urlTree.root.children.primary.segments.map((segment) => segment.path)
            .join('/');
        const exactMatch = urlString === item.route.replace(/\/$/, '');
        if (!exactMatch && item.routeTemplates) {
            for (let i = 0; i < item.routeTemplates.length; i++) {
                const result = new FormattedStringValueExtracter()
                    .Extract(urlString, item.routeTemplates[i]);
                if (result.IsMatch) {
                    return true;
                }
            }
        }
        return exactMatch;
    }

    scrollToCurrentMenuElement(): void {
        const path = location.pathname;
        const menuItem = document.querySelector('a[href=\'' + path + '\']');
        if (menuItem) {
            menuItem.scrollIntoView({ block: 'center' });
        }
    }

    getItemCssClasses(item: AppMenuItem, parentItem?: AppMenuItem) {
        let classes = 'menu-item';

        if (item.items.length) {
            if (!this.iconMenu) {
                classes += ' menu-accordion';
            } else {
                if (isNil(parentItem)) {
                    classes += ' menu-dropdown';
                } else {
                    classes += ' menu-accordion';
                }
            }
        }

        // custom class for menu item
        const customClass = objectPath.get(item, 'custom-class');
        if (customClass) {
            classes += ' ' + customClass;
        }

        if (this.iconMenu && isNil(parentItem)) {
            classes += ' pb-3';
        }

        if (!this.iconMenu && this.isMenuItemIsActive(item)) {
            classes += ' show';
        }

        return classes;
    }

    getSubMenuItemCssClass(item: AppMenuItem, parentItem?: AppMenuItem): string {
        let classes = 'menu-sub';

        if (!this.iconMenu) {
            classes += ' menu-sub-accordion';
        }

        const additionalClasses = isNil(this.iconMenu) || !isNil(parentItem)
            ? 'menu-sub-accordion'
            : 'menu-sub-dropdown px-1 py-4';

        return `${classes} ${additionalClasses}`;
    }

    toggleExpanded(item: AppMenuItem) {
        if (item.items.length) {
            item.expanded = !item.expanded;

            //Save accordion open/closed state
            if (item.expanded) {
                this.openAccordions.add(item);
            } else {
                this.openAccordions.delete(item);
            }
        }
    }

    onMenuItemClick(): void {
        if (this.isMobile) {
            this.sidebarService.closeSidebar();
        }
    }

}

export default SideBarMenuComponent;
